<script setup>
import { onMounted, ref, computed, watch } from 'vue';
// import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import Breadcrumb from '@/views/common/Breadcrumb.vue';
import useAuth from '@/composables/auth/useAuth';
import NotificationDropDown from '@/components/layout/NotificationDropDown';
import useEnv from '@/composables/app/useEnv';
import useToast from '@/composables/app/useToast';
import useAppConfig from '@/composables/app/useAppConfig';
import axios from 'axios';

const { showDefaultToast, showConfirmModal } = useToast();
const { websocketURL, serverUrl } = useEnv();
const store = useStore();
const router = useRouter();
const { currentBreakPoint } = useAppConfig();

const { authUser, changeRole } = useAuth();
const selectedLang = ref(null);
const countryList = ref(store.state.countryList);
// const i18n = useI18n();
const socketInstance = ref(null);
const avatarImageFailed = ref(false);
const roleSwitching = ref(null);
const canAccessAdminPanel = ref(false);

const checkSuperAdminAccess = async () => {
	if (!store.state.auth.accessToken) {
		canAccessAdminPanel.value = false;
		return;
	}

	const response = await axios.get('/users/super-admin/me/').catch(() => null);
	canAccessAdminPanel.value = Boolean(response?.data?.is_super_admin);
	store.commit('auth/setSuperAdminAccess', canAccessAdminPanel.value);
};

onMounted(() => {
	selectedLang.value = window.$appSetting.toggleLanguage();
	toggleMode();
	checkSuperAdminAccess();

	if (localStorage.getItem('user_id')) {
		const url = `${websocketURL}/${localStorage.getItem('user_id')}/`;
		socketInstance.value = new WebSocket(url);

		socketInstance.value.onmessage = function (event) {
			if (event?.data) {
				try {
					const eventData = JSON.parse(event?.data);
					if (eventData.message) {
						console.log(eventData?.message);
						store.commit(
							'notification/getNotificationRealtime',
							eventData.message
						);
						showDefaultToast('New Notification Arrived', 'success');
					}
				} catch (error) {
					console.log(error);
				}
			}
		};
	}
});

const toggleMode = mode => {
	window.$appSetting.toggleMode(mode);
};

// const changeLanguage = item => {
// 	selectedLang.value = item;
// 	window.$appSetting.toggleLanguage(item);

// 	i18n.locale.value = item.code;
// };

/**
 * ------------------------------------------------------------------------
 * logout
 * ------------------------------------------------------------------------
 */
const handleLogout = () => {
	if (socketInstance.value) {
		socketInstance.value.close(1000, 'User logged out');
	}
	store.commit('auth/accessToken', null);
	localStorage.removeItem('refresh');
	localStorage.clear();
	authUser.value = null;
	location.reload();
};

const onChangeRole = async role => {
	if (!role || role === current_role.value || roleSwitching.value) return;

	roleSwitching.value = role;
	try {
		await changeRole(role);
	} finally {
		roleSwitching.value = null;
	}
};

const onRoleSelect = async event => {
	const select = event.currentTarget;
	await onChangeRole(select.value);
	select.value = current_role.value || '';
};

const current_role = computed(() => {
	return store.state.auth.currentRole;
});

const profileInfo = computed(() => {
	return store.state.auth.profile;
});

const profileImageUrl = computed(() => {
	return profileInfo?.value?.photo
		? `${serverUrl}${profileInfo.value.photo}`
		: null;
});

const hasProfileImage = computed(
	() => Boolean(profileImageUrl.value) && !avatarImageFailed.value
);

const availableRoles = computed(() => {
	const roles = authUser.value?.role;
	if (Array.isArray(roles)) return [...new Set(roles.filter(Boolean))];
	return roles ? [roles] : [];
});

const userDisplayName = computed(() => {
	const fullName = [authUser.value?.first_name, authUser.value?.last_name]
		.filter(Boolean)
		.join(' ')
		.trim();
	return fullName || authUser.value?.username || 'User';
});

const userEmail = computed(() => authUser.value?.email || '');

const dashboardSwitcher = computed(() => {
	const onNewDashboard = router.currentRoute.value?.name === 'legacy-dashboard';
	return onNewDashboard
		? {
			to: { name: 'Home' },
			label: 'Classic Dashboard',
			badge: 'Default'
		}
		: {
			to: { name: 'legacy-dashboard' },
			label: 'New Dashboard',
			badge: 'Preview'
		};
});

watch(profileImageUrl, () => {
	avatarImageFailed.value = false;
});

watch(
	() => store.state.auth.accessToken,
	() => checkSuperAdminAccess()
);
</script>

<template>
	<div>
		<!--  BEGIN NAVBAR  -->
		<div class="header-container fixed-top">
			<header class="header navbar navbar-expand-sm f flex-between">
				<!-- logo -->
				<ul class="navbar-item theme-brand flex-row text-center">
					<li class="nav-item theme-logo">
						<router-link to="/">
							<img
								v-if="currentBreakPoint === 'xs'"
								src="/favicon.png"
								class="navbar-logo"
								alt="logo"
							/>

							<img
								v-else
								src="@/assets/images/logo.png"
								class="navbar-logo"
								alt="logo"
							/>
						</router-link>
					</li>
				</ul>

				<!-- menu items -->
				<div class="navbar-item flex-row ms-md-auto">
					<div class="dark-mode d-flex align-items-center">
						<a
							v-if="$store.state.dark_mode == 'light'"
							href="javascript:;"
							class="d-flex align-items-center"
							@click="toggleMode('dark')"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								stroke-width="2"
								stroke-linecap="round"
								stroke-linejoin="round"
								class="feather feather-sun"
							>
								<circle cx="12" cy="12" r="5"></circle>
								<line x1="12" y1="1" x2="12" y2="3"></line>
								<line x1="12" y1="21" x2="12" y2="23"></line>
								<line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
								<line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
								<line x1="1" y1="12" x2="3" y2="12"></line>
								<line x1="21" y1="12" x2="23" y2="12"></line>
								<line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
								<line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
							</svg>
							<span class="ms-2">Light</span>
						</a>
						<a
							v-if="$store.state.dark_mode == 'dark'"
							href="javascript:;"
							class="d-flex align-items-center"
							@click="toggleMode('system')"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								stroke-width="2"
								stroke-linecap="round"
								stroke-linejoin="round"
								class="feather feather-moon"
							>
								<path
									d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"
								></path>
							</svg>
							<span class="ms-2">Dark</span>
						</a>
						<a
							v-if="$store.state.dark_mode == 'system'"
							href="javascript:;"
							class="d-flex align-items-center"
							@click="toggleMode('light')"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								stroke-width="2"
								stroke-linecap="round"
								stroke-linejoin="round"
								class="feather feather-airplay"
							>
								<path
									d="M5 17H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-1"
								></path>
								<polygon points="12 15 17 21 7 21 12 15"></polygon>
							</svg>
							<span class="ms-2">System</span>
						</a>
					</div>

					<NotificationDropDown />

					<div class="dropdown nav-item user-profile-dropdown btn-group">
						<a
							href="javascript:;"
							id="ddluser"
							data-bs-toggle="dropdown"
							aria-expanded="false"
							class="btn dropdown-toggle btn-icon-only user nav-link profile-trigger"
						>
							<span class="profile-trigger-avatar">
								<img
									v-if="hasProfileImage"
									:src="profileImageUrl"
									:alt="`${userDisplayName} profile photo`"
									@error="avatarImageFailed = true"
								/>
								<span v-else class="profile-avatar-skeleton" aria-label="Default user avatar">
									<FeatherIcon type="user" size="18" />
								</span>
							</span>
						</a>
						<ul
							class="dropdown-menu dropdown-menu-right profile-menu"
							aria-labelledby="ddluser"
						>
							<li class="profile-summary" role="presentation">
								<div class="profile-summary-avatar">
									<img
										v-if="hasProfileImage"
										:src="profileImageUrl"
										:alt="`${userDisplayName} profile photo`"
										@error="avatarImageFailed = true"
									/>
									<span v-else class="profile-avatar-skeleton">
										<FeatherIcon type="user" size="21" />
									</span>
								</div>
								<div class="profile-summary-copy">
									<div class="profile-user-name">{{ userDisplayName }}</div>
									<div v-if="userEmail" class="profile-user-email">{{ userEmail }}</div>
									<span v-if="current_role" class="current-role-pill">
										{{ current_role }}
									</span>
								</div>
							</li>

							<li
								v-if="availableRoles.length > 1"
								class="role-switcher"
								role="presentation"
							>
								<div class="role-switcher-heading">
									<label for="profile-role-select">Switch role</label>
									<small>Choose how you want to work</small>
								</div>
								<div class="role-select-control">
									<span class="role-select-icon" aria-hidden="true">
										<FeatherIcon type="briefcase" size="15" />
									</span>
									<select
										id="profile-role-select"
										class="role-select"
										:value="current_role"
										:disabled="Boolean(roleSwitching)"
										aria-label="Switch active role"
										@change="onRoleSelect"
									>
										<option
											v-for="item in availableRoles"
											:key="item"
											:value="item"
										>
											{{ item }}{{ item === current_role ? ' — Current' : '' }}
										</option>
									</select>
									<span
										v-if="roleSwitching"
										class="spinner-border spinner-border-sm role-spinner"
										role="status"
										aria-label="Switching role"
									></span>
									<FeatherIcon
										v-else
										type="chevron-down"
										size="15"
										class="role-select-chevron"
									/>
								</div>
							</li>
							<li
								v-if="canAccessAdminPanel"
								class="profile-menu-actions"
								role="presentation"
							>
								<router-link
									:to="{ name: 'super-admin-tenants' }"
									class="dropdown-item profile-menu-item"
								>
									<span class="profile-menu-icon">
										<FeatherIcon type="shield" size="17" />
									</span>
									<span class="profile-menu-label">Admin Panel</span>
								</router-link>
							</li>
							<li class="profile-menu-actions" role="presentation">
								<router-link
									:to="dashboardSwitcher.to"
									class="dropdown-item profile-menu-item"
								>
									<span class="profile-menu-icon">
										<FeatherIcon type="bar-chart-2" size="17" />
									</span>
									<span class="profile-menu-label">{{ dashboardSwitcher.label }}</span>
									<span class="dashboard-badge">{{ dashboardSwitcher.badge }}</span>
								</router-link>
							</li>
							<li class="profile-menu-actions" role="presentation">
								<router-link
									:to="{ name: 'user-profile' }"
									class="dropdown-item profile-menu-item"
								>
									<span class="profile-menu-icon">
										<FeatherIcon type="user" size="17" />
									</span>
									<span class="profile-menu-label">Profile</span>
								</router-link>
							</li>
							<li class="profile-menu-actions sign-out-action" role="presentation">
								<router-link
									:disabled="true"
									event="click"
									to="/auth/login"
									class="dropdown-item profile-menu-item"
									@click="handleLogout"
								>
									<span class="profile-menu-icon">
										<FeatherIcon type="log-out" size="17" />
									</span>
									<span class="profile-menu-label">Sign Out</span>
								</router-link>
							</li>
						</ul>
					</div>
				</div>
			</header>
		</div>

		<!--  END NAVBAR  -->
		<!--  BEGIN NAVBAR  -->
		<div class="sub-header-container">
			<header class="header navbar navbar-expand-sm">
				<a
					href="javascript:void(0);"
					class="sidebarCollapse"
					data-placement="bottom"
					@click="$store.commit('toggleSideBar', !$store.state.is_show_sidebar)"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						width="24"
						height="24"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
						stroke-linejoin="round"
						class="feather feather-menu"
					>
						<line x1="3" y1="12" x2="21" y2="12"></line>
						<line x1="3" y1="6" x2="21" y2="6"></line>
						<line x1="3" y1="18" x2="21" y2="18"></line>
					</svg>
				</a>
				<Breadcrumb />
			</header>
		</div>
	</div>
</template>
<style lang="scss" scoped>
.role {
	visibility: visible !important;
	opacity: 1 !important;
}

.profile-trigger {
	display: flex;
	align-items: center;
	justify-content: center;
}

.profile-trigger-avatar,
.profile-summary-avatar {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	overflow: hidden;
	background: #eef2ff;
	border: 2px solid rgba(255, 255, 255, 0.32);
}

.profile-trigger-avatar {
	width: 32px;
	height: 32px;
}

.profile-trigger-avatar img,
.profile-summary-avatar img {
	width: 100% !important;
	height: 100% !important;
	border-radius: 50% !important;
	object-fit: cover;
}

.profile-avatar-skeleton {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;
	border-radius: 50%;
	color: #667085;
	background: linear-gradient(145deg, #f8fafc, #e4e7ec);
}

.profile-menu {
	width: 310px;
	padding: 0 !important;
	overflow: hidden;
	border: 1px solid #e4e7ec !important;
	border-radius: 12px !important;
	box-shadow: 0 18px 45px rgba(16, 24, 40, 0.16) !important;
}

.profile-summary {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 16px;
	background: linear-gradient(135deg, #f8faff, #f3f5ff);
	border-bottom: 1px solid #eaecf0;
}

.profile-summary-avatar {
	width: 44px;
	min-width: 44px;
	height: 44px;
	border-color: #d0d5dd;
}

.profile-summary-copy {
	min-width: 0;
}

.profile-user-name {
	color: #101828;
	font-size: 14px;
	font-weight: 700;
	line-height: 1.3;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.profile-user-email {
	color: #667085;
	font-size: 11px;
	line-height: 1.4;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.current-role-pill {
	display: inline-flex;
	margin-top: 5px;
	padding: 2px 7px;
	border-radius: 999px;
	background: #e9efff;
	color: #3451c6;
	font-size: 10px;
	font-weight: 700;
}

.role-switcher {
	padding: 13px 14px;
	border-bottom: 1px solid #eaecf0;
}

.role-switcher-heading {
	display: flex;
	flex-direction: column;
	margin-bottom: 9px;

	label {
		margin: 0;
		color: #344054;
		font-size: 12px;
		font-weight: 700;
	}

	small {
		color: #98a2b3;
		font-size: 10px;
	}
}

.role-select-control {
	position: relative;
	display: flex;
	align-items: center;
	width: 100%;
	border: 1px solid #d0d5dd;
	border-radius: 9px;
	background: #fff;
	box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
	transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.role-select-control:focus-within {
	border-color: #8098f9;
	box-shadow: 0 0 0 3px rgba(68, 97, 238, 0.12);
}

.role-select-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 36px;
	min-width: 36px;
	height: 38px;
	color: #475467;
}

.role-select {
	width: 100%;
	height: 38px;
	padding: 0 34px 0 0;
	border: 0;
	outline: 0;
	appearance: none;
	background: transparent;
	color: #344054;
	font-family: inherit;
	font-size: 12px;
	font-weight: 650;
	cursor: pointer;
}

.role-select:disabled {
	cursor: wait;
	opacity: 0.72;
}

.role-select-chevron,
.role-spinner {
	position: absolute;
	right: 11px;
	pointer-events: none;
}

.role-spinner {
	width: 13px;
	height: 13px;
	color: #4361ee;
}

.role-select-chevron {
	color: #667085;
}

.profile-menu-label {
	flex: 1;
}

.profile-menu-actions {
	border-bottom: 1px solid #f2f4f7;
}

.profile-menu-item {
	display: flex !important;
	align-items: center;
	gap: 10px;
	min-height: 44px;
	padding: 9px 14px !important;
	color: #344054 !important;
	font-size: 12px !important;
	font-weight: 600 !important;
	transition: background-color 0.18s ease, color 0.18s ease;

	&:hover {
		background: #f8f9fc !important;
		color: #3451c6 !important;
	}
}

.profile-menu-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 28px;
	height: 28px;
	border-radius: 8px;
	background: #f2f4f7;
}

.dashboard-badge {
	padding: 2px 7px;
	border-radius: 999px;
	background: #fff2e8;
	color: #b54708;
	font-size: 9px;
	font-weight: 800;
	letter-spacing: 0.3px;
	text-transform: uppercase;
}

.sign-out-action {
	border-bottom: 0;

	.profile-menu-item:hover {
		color: #d92d20 !important;
	}
}

</style>
